import { defaultCalculationConfig } from '@/config/default.config'
import type { Config, ConfigField } from '@/ts/types/config.types'
import type { FormField } from '@/ts/types/schema.types'

const configs: Record<string, Config> = {
  default: defaultCalculationConfig,
  mini: defaultCalculationConfig
}

/**
 * config.service handles the loading and application of configuration data for the leasing calculator.
 *
 * Configuration hierarchy (in order of precedence):
 * 1. Base config from default.config.ts
 * 2. Explicit Partner-Connect config override
 * 3. Valid prefill values applied after form hydration
 *
 * Application Process:
 * 1. Load base config for the given alias
 * 2. Apply custom config overrides if available
 * 3. Apply config to form fields:
 *    - Set default values for fields mapped via 'calculator' mapping
 *    - Set default values for fields mapped via 'config' mapping (only if current value is null/undefined)
 *    - Sync validation rules (min/max) with config values
 *    - Apply read-only states and field options
 */
export const ConfigService = {
  /**
   * Get config for the given alias
   * @param alias - Alias of the config to get
   * @returns Config for the given alias, with custom config applied
   */
  getConfig(alias: string, configOverride?: Config) {
    const baseConfig = structuredClone(configOverride ?? configs[alias])
    return {
      data: baseConfig
    }
  },

  /**
   * Apply the config to the form
   * @param config - Config to apply
   * @param mappingStore - Mapping store
   * @param findFormField - Function to find the form field
   * @param generateField - Function to generate the form field
   */
  async applyConfigToForm(config: Config, mappingStore: any, findFormField: Function, generateField: Function): Promise<void> {
    const callbacks = config.fields.map(async (configField: ConfigField) => {
      await mappingStore.iterateFieldIdsOfKey('calculator', configField.name, async (fieldId: string) => {
        await findFormField(generateField(fieldId, null), (formField: FormField) => {
          // Only set default if field is not mapped via 'config'
          const hasConfigMapping = mappingStore.getFieldIdsOfKey('config', configField.name)?.length > 0
          if (!hasConfigMapping) {
            formField.config.value = configField.default
          }
          formField.config.items = configField.items
          formField.config.read_only = configField.read_only
          formField.config.hidden_by_config = configField.hide === true
          formField.config.hide = configField.hide === true || formField.config.hide === true

          // Sync min/max and validation-rule args
          ConfigService.syncValidationRulesWithConfig(formField, configField)
        })
      })
    })

    await Promise.allSettled(callbacks)

    // Apply defaults for fields mapped via 'config'
    await ConfigService.applyConfigDefaults(config, mappingStore, findFormField, generateField)
  },

  /**
   * Sync the validation rules with the config
   * @param formField - Form field to sync the validation rules with
   * @param configField - Config field to sync the validation rules with
   */
  syncValidationRulesWithConfig(formField: FormField, configField: any): void {
    // Update field config with new values
    formField.config.min = configField.min ?? formField.config.min
    formField.config.max = configField.max ?? formField.config.max

    // Sync validation rules with the new min/max values
    ConfigService.syncRulesWithMinMax(formField.config.field_rules, configField.min, configField.max)
  },

  /**
   * Sync the validation rules with the min/max values
   * @param fieldRules - Field rules to sync
   * @param min - Min value to sync
   * @param max - Max value to sync
   */
  syncRulesWithMinMax(fieldRules: any[], min?: number | null, max?: number | null): void {
    const priceBetweenRule = fieldRules?.find((r: any) => r.type === 'price_between')
    if (priceBetweenRule && min !== null && max !== null && min !== undefined && max !== undefined) {
      const minNum = Number(min)
      const maxNum = Number(max)
      if (!isNaN(minNum) && !isNaN(maxNum)) {
        priceBetweenRule.arg = [minNum, maxNum]
      }
    }
  },

  /**
   * Apply config defaults to the form fields
   * @param config - Config to apply the defaults to
   * @param mappingStore - Mapping store
   * @param findFormField - Function to find the form field
   * @param generateField - Function to generate the form field
   */
  async applyConfigDefaults(config: Config, mappingStore: any, findFormField: Function, generateField: Function): Promise<void> {
    const defaultCallbacks = config.fields.map(async (configField: ConfigField) => {
      const mappedFieldIds = mappingStore.getFieldIdsOfKey('config', configField.name) ?? []
      if (!mappedFieldIds || mappedFieldIds.length === 0) return

      const setTasks = mappedFieldIds.map(async (fieldId: string) => {
        await findFormField(generateField(fieldId, null), (formField: FormField) => {
          const currentValue = formField?.config?.value
          // Only apply default value if the field is currently empty (null or undefined)
          // This prevents overwriting existing user input for fields with 'config' mapping
          if (currentValue === null || currentValue === undefined) {
            formField.config.value = configField.default
          }
        })
      })

      await Promise.all(setTasks)
    })

    return Promise.allSettled(defaultCallbacks).then(() => {})
  }
}
